import translations from './excel.translations.json';

export * from './excel';
export { countActiveFilters, filterString } from './filters';
export * from './dateUtils';
export * from './formatters';
export * from './schema';
export * from './warningMessage';
export * from './sorters';
export * from './useDirectoryNavigation';
export * from './usePageSizeOptions';
export * from './randomUniqueKey';
export * from './objectMeta';
export * from './getMapBy';
export * from './omit';
export * from './options';

export const cleanArray = <T>(
  array?: Array<T>,
): typeof array extends undefined
  ? undefined
  : Exclude<T, undefined | null | '' | false | 0>[] =>
  array?.filter(Boolean) as any;

export const removeNullish = <T extends Record<PropertyKey, any>>(
  record: T,
): Omit<
  T,
  {
    [K in keyof T]: T[K] extends null | undefined ? K : never;
  }[keyof T]
> => {
  const { ...copy } = record as Record<PropertyKey, any>;
  for (const key in record) {
    if (copy[key] === null || copy[key] === undefined) {
      delete (copy as any)[key];
    }
  }
  return copy as any;
};

export const excelTranslations = translations;

export const isSafeFiniteInteger = (x: any): x is number =>
  !(
    isNaN(x) ||
    !Number.isSafeInteger(Number(x)) ||
    !Number.isFinite(Number(x))
  );

export const parseSafeInteger = (x: any): number | undefined =>
  isSafeFiniteInteger(x) ? Number(x) : undefined;

export const isStringWithLength = (
  value: any,
): value is string & { length: Exclude<number, 0> } =>
  typeof value === 'string' && Boolean(value.length);

export const isStringWithOnlyNumbers = (value: any) =>
  isStringWithLength(value) &&
  !isNaN(value as any) &&
  !isNaN(parseFloat(value));

export const isObject = (value?: any): value is Record<PropertyKey, any> =>
  typeof value === 'object' && value !== null;

export const isObjectWith = <Props extends Record<PropertyKey, any>>(
  value: any,
  props: Props,
): value is Props =>
  isObject(value) &&
  Object.entries(props).every(
    ([key, val]) =>
      Object.prototype.hasOwnProperty.call(value, key) &&
      typeof value[key] === typeof val,
  );

const STRING_KEY_DELIMITER = '~';

export type StringKeyDelimiter = typeof STRING_KEY_DELIMITER;

export type StringKey<T extends readonly (string | number)[]> = T extends []
  ? ''
  : T extends [infer First, ...infer Rest]
    ? First extends string | number
      ? Rest extends readonly (string | number)[]
        ? Rest['length'] extends 0
          ? `${First}`
          : `${First}${StringKeyDelimiter}${StringKey<Rest>}`
        : never
      : never
    : string;

export const encodeStringKey = <T extends (string | number)[]>(
  ...keyElements: T
) => `${keyElements.join(STRING_KEY_DELIMITER)}` as StringKey<T>;

export const decodeStringKey = (key: string): string[] =>
  key.split(STRING_KEY_DELIMITER);
